import { IeLynxObject } from "../../common/models/eLynxObject";
import { RestError } from "../../services/rest-service/RestService";
import { useMutation, useQueryClient } from "react-query";
import { IObjectHistoryModel } from "../../services/history-data-service/Models";
import MemosDataService from "../../services/memos-data-service/MemosDataService";
import { keys } from "./Keys";
import { IObjectHistoryItem, MutationContextType, ObjectHistoryItemTypeEnum, toMemoModel, IObjectHistoryMemoItem } from "./Common";
import { BaseUseMutationParameters } from "../BaseUseMutationParameters";
import { DurationOptionEnum } from "../../components/custom-duration-dialog/CustomDurationUtils";



export type UseDeletetHistoryItemsQueryParams = {
    customerID: number,
    groupID: number,
    eLynxObject: IeLynxObject,
    offline: boolean,
    duration?: DurationOptionEnum,
    startDate?: Date,
    endDate?: Date,
} & BaseUseMutationParameters<void, IObjectHistoryItem>;


export function useDeleteHistoryItem(params: UseDeletetHistoryItemsQueryParams) {
    const queryClient = useQueryClient();

    return useMutation<void, RestError, IObjectHistoryItem, MutationContextType>(
        (item) => {
            switch (item.type) {
                case ObjectHistoryItemTypeEnum.Memo:
                    const memoModel = toMemoModel(item as IObjectHistoryMemoItem);

                    return MemosDataService.deleteMemo(
                        params.customerID,
                        params.groupID,
                        memoModel,
                        params.offline
                    );
                default:
                    throw new Error("Cannot delete specified history item type. Only memos are supported at this time.");
            }
        },
        {
            onMutate: async (item: IObjectHistoryItem) => {

                await queryClient.cancelQueries(keys.all);

                const previousHistoryItems = queryClient.getQueryData<IObjectHistoryModel>(
                    keys.list(
                        params.customerID,
                        params.groupID,
                        params.eLynxObject,
                        params.offline,
                        params.duration,
                        params.startDate,
                        params.endDate
                    )
                );

                queryClient.setQueryData<IObjectHistoryModel>(
                    keys.list(
                        params.customerID,
                        params.groupID,
                        params.eLynxObject,
                        params.offline,
                        params.duration,
                        params.startDate,
                        params.endDate
                    ),
                    (old?: IObjectHistoryModel) => {

                        switch (item.type) {
                            case ObjectHistoryItemTypeEnum.Memo:
                                return {
                                    alarmHistory: old?.alarmHistory ?? [],
                                    controlHistory: old?.controlHistory ?? [],
                                    dataQualityEvents: old?.dataQualityEvents ?? [],
                                    diagnosticEvents: old?.diagnosticEvents ?? [],
                                    formEvents: old?.formEvents ?? [],
                                    memos: old?.memos?.filter(m => m.memoID !== (item as IObjectHistoryMemoItem).memoID) ?? []
                                } as IObjectHistoryModel;

                            default:
                                throw new Error("Connot delete specified history item type. Only memos are supported at this time.");
                        }
                    }
                );

                return {
                    previousHistoryItems
                };
            },
            onError: params.onError,
            onSuccess: params.onSuccess,
            onSettled: (_data, error, _item, context) => {

                if (error && context?.previousHistoryItems) {
                    queryClient.setQueryData<IObjectHistoryModel>(
                        keys.list(
                            params.customerID,
                            params.groupID,
                            params.eLynxObject,
                            params.offline,
                            params.duration,
                            params.startDate,
                            params.endDate
                        ),
                        context?.previousHistoryItems
                    );
                }

                queryClient.invalidateQueries(keys.lists());
            }
        }
    );
}
